import {
	Client,
	TokenProvider,
	RegistrationState,
} from "@pusher/push-notifications-web"
import config from "../config"
import { getXSRFToken, xhr } from "@sembark-travel/xhr"
import { logError } from "@sembark-travel/logging"
import { useEffect } from "react"

type TUser = { uid: string }

let beamsClient: undefined | Client = undefined

let waitForServiceWorker = (_: ServiceWorkerRegistration) => {
	// this will be replaced by a resolver, so DO NOT PUT anything here
}

export function setCurrentSWRegistrationForNotications(
	registration: ServiceWorkerRegistration
) {
	waitForServiceWorker(registration)
}

const serviceWorkerIsReady = new Promise<ServiceWorkerRegistration>(
	(resolve) => {
		waitForServiceWorker = resolve
	}
)

async function getClient() {
	const swRegistration = await serviceWorkerIsReady
	if (!beamsClient) {
		beamsClient = new Client({
			instanceId: config.services.pusher_beams.instanceId,
			serviceWorkerRegistration: swRegistration,
		})
	}
	return beamsClient
}

// Every flow (login sync, Preferences, logout) mutates the SAME Beams client.
// Run these ops one-at-a-time so a concurrent stop() can't land between another
// flow's start() and setUserId() — which throws
// ".start must be called before .setUserId".
let pushOpQueue: Promise<unknown> = Promise.resolve()
function serializePushOp<T>(op: () => Promise<T>): Promise<T> {
	const result = pushOpQueue.then(op, op)
	// keep the chain alive regardless of this op's outcome
	pushOpQueue = result.then(
		() => undefined,
		() => undefined
	)
	return result
}

/**
 * Resolves once every currently-queued push op has settled. Consumers that read
 * the registration state (e.g. the nudge) await this first so they don't observe
 * the transient not-registered window mid-way through a stop()/start() flow.
 */
export function whenPushOpsIdle(): Promise<void> {
	return pushOpQueue.then(
		() => undefined,
		() => undefined
	)
}

// The device's subscription state changes asynchronously (login auto-resubscribe,
// Preferences, logout). Anything showing that state reactively subscribes here so
// it re-reads once a flow settles, instead of latching onto a mid-flow snapshot.
const pushSubscriptionChangeListeners = new Set<() => void>()

export function subscribeToPushSubscriptionChanges(
	listener: () => void
): () => void {
	pushSubscriptionChangeListeners.add(listener)
	return () => {
		pushSubscriptionChangeListeners.delete(listener)
	}
}

function notifyPushSubscriptionChanged() {
	pushSubscriptionChangeListeners.forEach((listener) => {
		try {
			listener()
		} catch (e) {
			console.log(e)
		}
	})
}

export async function unsubscribeUserDeviceForPushNotifications({
	keepLastSubscriber = false,
}: { keepLastSubscriber?: boolean } = {}) {
	return await serializePushOp(async () => {
		try {
			const client = await getClient()
			await client.stop()
		} catch (e) {
			const error = e as Error
			console.error(error)
			alert(
				error.message ||
					"Unable to unsubscribe the device. Please try after sometime."
			)
		}
		// On logout we stop the device but keep the last-subscriber marker so the
		// SAME user re-logging in auto-resubscribes (see
		// syncUserDevicePushNotificationsSubscriptionStatus). A manual/explicit
		// unsubscribe clears it so it stays off until re-enabled.
		if (!keepLastSubscriber) {
			setLastSubscriberUserId("")
		}
		return false
	}).finally(notifyPushSubscriptionChanged)
}

const LAST_SUBSCRIBER_STORAGE_KEY = "bpn_subscriber_user"

function setLastSubscriberUserId(id: string) {
	try {
		window.localStorage.setItem(LAST_SUBSCRIBER_STORAGE_KEY, id)
	} catch (e) {
		console.log(e)
	}
}

function getLastSubscriberUserId(): string | null {
	try {
		const id = String(window.localStorage.getItem(LAST_SUBSCRIBER_STORAGE_KEY))
		if (!id) return null
		return id
	} catch (e) {
		console.log(e)
	}
	return null
}

// Remember a failed registration so the auto flows (login sync, Preferences
// auto-subscribe, the nudge) stop retrying on every load/visit — e.g. a browser
// that blocks the push service would otherwise be hammered forever. An explicit
// user action (the manual Register button) clears this and tries again.
const REGISTRATION_FAILED_STORAGE_KEY = "bpn_registration_failed_user"

function markPushRegistrationFailed(userId: string) {
	try {
		window.localStorage.setItem(REGISTRATION_FAILED_STORAGE_KEY, userId)
	} catch (e) {
		console.log(e)
	}
}

export function clearPushRegistrationFailure() {
	try {
		window.localStorage.removeItem(REGISTRATION_FAILED_STORAGE_KEY)
	} catch (e) {
		console.log(e)
	}
}

export function isPushRegistrationSuppressed(user: TUser): boolean {
	try {
		return (
			window.localStorage.getItem(REGISTRATION_FAILED_STORAGE_KEY) ===
			getUserIdForNotification(user)
		)
	} catch (e) {
		console.log(e)
		return false
	}
}

export function useSyncUserDevicePushNotificationsSubscriptionStatus(
	user?: TUser
) {
	const { uid: userId } = user || {}
	useEffect(() => {
		if (userId) {
			syncUserDevicePushNotificationsSubscriptionStatus({ uid: userId })
		}
	}, [userId])
}

async function syncUserDevicePushNotificationsSubscriptionStatus(user: TUser) {
	// One serialized op for the whole sync so its stop()/start()/setUserId()
	// never interleaves with a concurrent subscribe/unsubscribe. Calls the
	// UNLOCKED internal subscribe to avoid a nested-lock deadlock.
	return await serializePushOp(async () => {
		try {
			await unsubscribeIfIncorrectUserForPushNotifications(user).catch(
				() => undefined
			)
			const lastSubscriberId = getLastSubscriberUserId()
			if (
				lastSubscriberId &&
				getUserIdForNotification(user) === lastSubscriberId &&
				// Don't auto-retry a registration that already failed on this
				// device — wait for an explicit manual attempt.
				!isPushRegistrationSuppressed(user)
			) {
				console.info("Auto-Subscribed last subscriber.")
				await subscribeUserDeviceInternal(user)
			}
			// sync the stored subscriber id
			const subscriber_id = await getCurrentSubscribedUserId()
			if (subscriber_id) {
				setLastSubscriberUserId(subscriber_id)
			} else {
				// keep the last subscriber id
			}
		} catch (e) {
			console.error(e)
		} finally {
			// Let reactive consumers (the nudge) re-read the now-settled state,
			// so a mid-sync not-registered snapshot doesn't linger until a reload.
			notifyPushSubscriptionChanged()
		}
	})
}

async function unsubscribeIfIncorrectUserForPushNotifications(user: TUser) {
	const client = await getClient()
	// Checking the browser is associated with the correct user
	const userId = await client.getUserId()
	// Check if the Beams user matches the current user
	if (userId && userId !== getUserIdForNotification(user)) {
		console.warn("Incorrect user registered with device!")
		// Unregister for notifications
		try {
			await client.stop()
		} catch (e) {
			console.log(e)
		}
	}
}

function getUserIdForNotification(user: TUser) {
	return "users." + user.uid
}

async function getCurrentSubscribedUserId(): Promise<undefined | string> {
	try {
		const beamsClient = await getClient()
		// Checking the browser is associated with the correct user
		return beamsClient.getUserId()
	} catch (e) {
		console.log(e)
	}
	return undefined
}

export async function subscribeUserDeviceForPushNotifications(user: TUser) {
	return await serializePushOp(() => subscribeUserDeviceInternal(user)).finally(
		notifyPushSubscriptionChanged
	)
}

// Transient Beams registration failures that clear on a fresh start(): a
// stale/rotated browser push subscription, or a device registration that hasn't
// propagated on Beams yet, 401s with "subscription is not registered". Our
// catch stop()s the device, so re-registering once recovers it.
function isTransientBeamsRegistrationError(error: unknown): boolean {
	const message = (error instanceof Error ? error.message : "").toLowerCase()
	return (
		message.includes("subscription is not registered") ||
		message.includes("token error") ||
		message.includes("401")
	)
}

async function subscribeUserDeviceInternal(
	user: TUser,
	attempt = 0
): Promise<void> {
	const beamsClient = await getClient()
	if (await isUserDeviceSubscribedForPushNotifications(user)) {
		// already setup
		return
	}

	const subscriptionUserId = getUserIdForNotification(user)

	// Checking the browser is associated with the correct user
	try {
		await unsubscribeIfIncorrectUserForPushNotifications(user)
		await beamsClient.start() // start and register the device
		const { headerName: xsrfHeaderName, value: xsrfValue } = getXSRFToken(xhr)
		// register the user
		const beamsTokenProvider = new TokenProvider({
			url: xhr.defaults.baseURL + "/push-notifications/auth",
			credentials: "include",
			headers: {
				"X-App-Version": config.appVersion,
				Accept: "application/json",
				[xsrfHeaderName]: xsrfValue,
				"content-type": "application/x-www-form-urlencoded",
			},
		})
		await beamsClient.setUserId(subscriptionUserId, beamsTokenProvider)
		// Mark opt-in only AFTER a successful registration, and clear any prior
		// failure so future loads may auto-resubscribe.
		setLastSubscriberUserId(subscriptionUserId)
		clearPushRegistrationFailure()
	} catch (e) {
		// stop() clears the (possibly stale) device subscription before we retry
		// or give up.
		await beamsClient.stop().catch(() => undefined)
		// Recover a transient stale-subscription failure once, transparently —
		// don't surface it or suppress the device.
		if (attempt === 0 && isTransientBeamsRegistrationError(e)) {
			await new Promise((resolve) => setTimeout(resolve, 800))
			return subscribeUserDeviceInternal(user, attempt + 1)
		}
		// Remember a real failure so the auto flows stop retrying every load.
		markPushRegistrationFailed(subscriptionUserId)
		logError(e)
		throw e
	}
	console.log("Successfully registered device and subscribed!")
}

export async function getUserDeviceSubscriptionStateForPushNotifications(
	user: TUser
): Promise<RegistrationState> {
	const client = await getClient()
	if (!client) {
		return RegistrationState.PERMISSION_DENIED
	}
	const subscriber_id = await getCurrentSubscribedUserId()
	if (subscriber_id !== getUserIdForNotification(user)) {
		return RegistrationState.PERMISSION_DENIED
	}
	return client.getRegistrationState()
}

export async function isUserDeviceSubscribedForPushNotifications(
	user: TUser
): Promise<boolean> {
	try {
		return (
			(await getUserDeviceSubscriptionStateForPushNotifications(user)) ===
			RegistrationState.PERMISSION_GRANTED_REGISTERED_WITH_BEAMS
		)
	} catch (e) {
		console.log(e)
	}

	return false
}

export function askNotificationPermission(
	onSuccess: (status: NotificationPermission) => void
) {
	if (hasDeviceAllowedNotifications()) {
		onSuccess("granted")
		return
	}
	function handlePermission(permission: NotificationPermission) {
		// Whaeger the user ansers, we make sure Chrome stores the information
		if (!Notification.permission) {
			;(Notification as { permission: NotificationPermission }).permission =
				permission
		}

		onSuccess(permission || "default")
	}

	// Check whether browser supports the promise version of requestPermission()
	// Safari only supports the old callback-based version
	function checkNotificationPromise() {
		try {
			Notification.requestPermission().then()
		} catch (e) {
			return false
		}
		return true
	}

	if (!doesDeviceSupportNotifications()) {
		console.log("This browser/device does not support notifications")
		handlePermission("default")
	} else {
		// use promise based or callback based request
		if (checkNotificationPromise()) {
			Notification.requestPermission().then(handlePermission)
		} else {
			Notification.requestPermission(handlePermission)
		}
	}
}

// Check if the browser supports notifications
export function doesDeviceSupportNotifications(): boolean {
	return typeof window !== "undefined" && Boolean(window.Notification)
}

// Pusher Beams web SDK explicitly supports only Chrome, Firefox, Edge, Opera.
// Safari / iOS use APNs-based push that Beams does not support.
function isSupportedBrowserForPush(): boolean {
	if (typeof window === "undefined" || typeof navigator === "undefined")
		return false
	const winNav = window.navigator
	const ua = winNav.userAgent
	const isChromium =
		(window as never as { chrome: unknown }).chrome !== null &&
		typeof (window as never as { chrome: unknown }).chrome !== "undefined"
	const isOpera = ua.indexOf("OPR") > -1
	const isEdge = ua.indexOf("Edg") > -1
	const isFirefox = ua.indexOf("Firefox") > -1
	const isChrome =
		isChromium && winNav.vendor === "Google Inc." && !isEdge && !isOpera
	return isChrome || isOpera || isFirefox || isEdge
}

/**
 * Whether this browser can actually register for Pusher Beams web push.
 * Stricter than doesDeviceSupportNotifications(): Beams needs a service worker
 * + PushManager, and only supports Chrome, Firefox, Edge, Opera.
 */
export function doesBrowserSupportPushNotifications(): boolean {
	return (
		doesDeviceSupportNotifications() &&
		typeof navigator !== "undefined" &&
		"serviceWorker" in navigator &&
		typeof window !== "undefined" &&
		"PushManager" in window &&
		isSupportedBrowserForPush()
	)
}

export function getDeviceNotificationPermissionsStatus(): NotificationPermission {
	return (
		(doesDeviceSupportNotifications() ? Notification.permission : undefined) ||
		"default"
	)
}

export function hasDeviceAllowedNotifications(): boolean {
	return getDeviceNotificationPermissionsStatus() === "granted"
}

type BraveNavigator = Navigator & {
	brave?: { isBrave?: () => Promise<boolean> }
}

export async function isBraveBrowser(): Promise<boolean> {
	try {
		return Boolean(await (navigator as BraveNavigator).brave?.isBrave?.())
	} catch {
		return false
	}
}

/**
 * Turn a Beams registration failure into actionable guidance. Chromium's Web
 * Push routes through Google's push service; privacy browsers (Brave, de-
 * Googled Chromium) disable it by default, so start()/subscribe() rejects with
 * a "push service error" even though the notification permission was granted.
 */
export async function describePushRegistrationError(
	error: unknown
): Promise<string> {
	const message = (error instanceof Error ? error.message : "").toLowerCase()
	const isPushServiceError =
		message.includes("push service") || message.includes("registration failed")
	if (isPushServiceError) {
		if (await isBraveBrowser()) {
			return "Brave blocks Google push messaging by default, which notifications need. Open brave://settings/privacy, turn on “Use Google services for push messaging”, restart Brave, and try registering again."
		}
		return "This browser has push messaging disabled, which notifications need. Enable Google/push messaging in your browser’s privacy settings, restart the browser, and try again."
	}
	return error instanceof Error && error.message
		? error.message
		: "Something went wrong"
}
